import { type RefObject, useLayoutEffect } from "react";

/**
 * Marks which edges of a sideways-scrolling row have more past them, as `data-edge` of `l`, `r`
 * or `lr`, for the `edge-fade` class to fade out. With nothing overflowing it is cleared, so a
 * row that wraps on a wide screen draws no fade.
 */
export const useEdgeFade = (ref: RefObject<HTMLElement>, enabled = true) => {
    useLayoutEffect(() => {
        const row = ref.current;
        if (!row || !enabled) return;

        const update = () => {
            const left = row.scrollLeft > 2;
            const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;

            row.dataset.edge = `${left ? "l" : ""}${right ? "r" : ""}`;
        };

        update();

        row.addEventListener("scroll", update, { passive: true });

        /** A count or label changing alters the row's width without resizing the row itself */
        const resized = new ResizeObserver(update);
        resized.observe(row);
        const changed = new MutationObserver(update);
        changed.observe(row, { childList: true, subtree: true, characterData: true });

        return () => {
            row.removeEventListener("scroll", update);
            resized.disconnect();
            changed.disconnect();
        };
    }, [ref, enabled]);
};
